import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    OnDestroy,
    Renderer2,
    viewChildren
} from '@angular/core';
import { Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { VisibilityService } from '@df/ui/common/visibility.service';
import { PhotoSwipeModule } from '@df/ui/photoswipe/photoswipe.module';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { Nexus } from '@tomandco/nexus-api-types';
import { LocalVimeoPlayerComponent } from '../../../ui/display/media/vimeo-player.component';

@Component({
    selector: 'product-media',
    templateUrl: './product-media.component.html',
    styleUrl: './product-media.component.scss',
    standalone: true,
    imports: [CommonModule, DfImageDirective, LocalVimeoPlayerComponent, PhotoSwipeModule, ShellComponent, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductMediaComponent extends UiComponent implements AfterViewInit, OnDestroy {
    media = input<Nexus.Lib.Catalog.Type.IProductMediaItem[]>([]);
    product = input<Product>();

    readonly sections = viewChildren<ElementRef<HTMLElement>>('sectionRef');

    parent = computed(() => this.product()?.parent);

    protected readonly app = inject(App);
    protected readonly renderer = inject(Renderer2);
    protected readonly elementRef = inject(ElementRef);
    protected readonly scrollToService = inject(ScrollToService);
    protected readonly visibilityService = inject(VisibilityService);

    private observer?: IntersectionObserver;

    ngAfterViewInit() {
        if (this.app.isPlatformBrowser) {
            const sections = [...this.sections()].sort((a, b) => {
                const aIndex = Number(a.nativeElement.getAttribute('data-index') ?? 0);
                const bIndex = Number(b.nativeElement.getAttribute('data-index') ?? 0);

                return aIndex - bIndex;
            });

            this.observer = new IntersectionObserver(
                (entries: IntersectionObserverEntry[]) =>
                    entries.forEach(entry => {
                        const index = entry.target.getAttribute('data-index');

                        const item = this.elementRef.nativeElement.querySelector(`[id="scroll-to-section-${index}"]`);

                        item.classList.remove('is-active');

                        if (!entry.isIntersecting) return;

                        item.classList.add('is-active');
                    }),
                {
                    rootMargin: '-10% 0% -90% 0%'
                }
            );

            sections.forEach(section => {
                this.observer?.observe(section.nativeElement);
            });
        }
    }

    override ngOnDestroy() {
        this.observer?.disconnect();

        super.ngOnDestroy();
    }

    scrollToSection(section: string) {
        this.scrollToService.scrollTo(section, { offset: 55 });
    }
}
